import * as THREE from 'three';
import { mulberry32 } from '../core/rng';

/**
 * GPU-driven snowfall.
 *
 * Implementation choice: a single THREE.Points draw call whose flake
 * positions are pure functions of (per-flake seed, time, wind) evaluated in
 * the vertex shader. Zero per-frame CPU work, one draw call, and the count
 * scales from 0 to ~30k just by changing the geometry draw range. Instanced
 * meshes would only pay off for true 3D flakes, which soft billboards beat
 * visually at this scale anyway. Because flakes derive purely from a seed,
 * two clients with the same seed see the same storm — deterministic by
 * construction for future multiplayer.
 *
 * The flake volume is a box that wraps around the camera, so snow is always
 * falling around the player without simulating the whole map.
 */

export interface SnowfallOptions {
  seed: number;
  maxParticles: number;
  /** Wrap-box size (x/z extent and height). */
  areaSize?: number;
  areaHeight?: number;
}

export class Snowfall {
  readonly points: THREE.Points;
  private material: THREE.ShaderMaterial;
  private geometry: THREE.BufferGeometry;
  private maxParticles: number;
  /** 0..1 storm intensity. */
  intensity = 0;

  constructor(options: SnowfallOptions) {
    this.maxParticles = options.maxParticles;
    const area = options.areaSize ?? 90;
    const height = options.areaHeight ?? 30;

    const rand = mulberry32(options.seed);
    const seeds = new Float32Array(this.maxParticles * 3);
    const sizes = new Float32Array(this.maxParticles);
    for (let i = 0; i < this.maxParticles; i++) {
      seeds[i * 3] = rand();
      seeds[i * 3 + 1] = rand();
      seeds[i * 3 + 2] = rand();
      sizes[i] = 0.6 + rand() * 0.9;
    }

    this.geometry = new THREE.BufferGeometry();
    // Positions are computed in the shader; the attribute is just per-flake seeds.
    this.geometry.setAttribute('position', new THREE.BufferAttribute(seeds, 3));
    this.geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 10000);

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uCamPos: { value: new THREE.Vector3() },
        uArea: { value: new THREE.Vector3(area, height, area) },
        uFallSpeed: { value: 1.2 },
        uWind: { value: 0.3 },
        uPixelRatio: { value: 1 },
        uOpacity: { value: 0.85 },
      },
      vertexShader: /* glsl */ `
        attribute float aSize;
        uniform float uTime;
        uniform vec3 uCamPos;
        uniform vec3 uArea;
        uniform float uFallSpeed;
        uniform float uWind;
        uniform float uPixelRatio;
        varying float vFade;

        void main() {
          vec3 seed = position; // each component in [0,1)
          float speed = uFallSpeed * (0.7 + 0.6 * seed.x);
          vec3 p;
          p.x = seed.x * uArea.x + uTime * uWind * (0.5 + seed.z);
          p.y = seed.y * uArea.y - uTime * speed;
          p.z = seed.z * uArea.z + sin(uTime * (0.6 + seed.y) + seed.x * 6.2831) * (0.4 + uWind);
          // Wrap the volume around the camera.
          vec3 rel = mod(p - uCamPos, uArea);
          vec3 world = uCamPos + rel - uArea * 0.5;
          vec4 mv = viewMatrix * vec4(world, 1.0);
          float dist = max(-mv.z, 0.1);
          gl_PointSize = aSize * 5.2 * uPixelRatio / dist;
          vFade = smoothstep(uArea.x * 0.55, uArea.x * 0.28, dist);
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uOpacity;
        varying float vFade;
        void main() {
          float d = length(gl_PointCoord - 0.5) * 2.0;
          float a = smoothstep(1.0, 0.35, d) * vFade * uOpacity;
          if (a < 0.02) discard;
          gl_FragColor = vec4(0.93, 0.95, 1.0, a);
        }
      `,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.points.renderOrder = 10;
    this.setIntensity(0);
  }

  setIntensity(intensity: number): void {
    this.intensity = THREE.MathUtils.clamp(intensity, 0, 1);
    // Particle count ramps non-linearly so "light flurries" reads sparse.
    const count = Math.floor(this.maxParticles * Math.pow(this.intensity, 1.4));
    this.geometry.setDrawRange(0, count);
    this.points.visible = count > 0;
    const u = this.material.uniforms;
    u.uFallSpeed!.value = 1.0 + this.intensity * 2.4;
    u.uWind!.value = 0.15 + this.intensity * 1.6;
    u.uOpacity!.value = 0.65 + this.intensity * 0.3;
  }

  setMaxParticles(max: number, seed: number): void {
    if (max === this.maxParticles) return;
    // Rebuild attributes at the new pool size.
    const rand = mulberry32(seed);
    const seeds = new Float32Array(max * 3);
    const sizes = new Float32Array(max);
    for (let i = 0; i < max; i++) {
      seeds[i * 3] = rand();
      seeds[i * 3 + 1] = rand();
      seeds[i * 3 + 2] = rand();
      sizes[i] = 0.6 + rand() * 0.9;
    }
    this.maxParticles = max;
    this.geometry.setAttribute('position', new THREE.BufferAttribute(seeds, 3));
    this.geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
    this.setIntensity(this.intensity);
  }

  update(elapsed: number, camera: THREE.Camera, pixelRatio: number): void {
    const u = this.material.uniforms;
    u.uTime!.value = elapsed;
    (u.uCamPos!.value as THREE.Vector3).setFromMatrixPosition(camera.matrixWorld);
    u.uPixelRatio!.value = pixelRatio;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
